/**
 * `AW4` · La tabla de la pantalla «Conversaciones».
 *
 * Las diez columnas del PRD, el orden invariante, el paginado, los filtros de
 * rango de fechas y teléfono, y los DOS estados vacíos distinguidos con
 * `total`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LAS DECISIONES QUE ESTE ARCHIVO LLEVA CON `guard`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ① **No se importa `modules/**`.** Lo único que entra del repo son TIPOS de
 *   `contracts/conversation`, y el transporte es HTTP contra `admin-web`
 *   (`conversaciones/borde-conversaciones.ts`). `management-ui` es un
 *   **artefacto estático** en S3 + CloudFront, no un proceso: un import de
 *   `modules/conversation` compila, anda en el dev server de quien lo escribe, y
 *   no existe en producción, donde no hay Node que lo ejecute ni credencial de
 *   base. Y `listar` recibe una `TransactionalUnit` — abrir esa capa desde el
 *   navegador es abrir una conexión a Postgres desde la única unidad que ve el
 *   contenido crudo de ~16.000 menores.
 *
 * ② **No hay acción de abrir, editar ni responder, y ninguna fila navega a una
 *   pantalla de detalle.** `P0-3` lo prohíbe y el motivo no es de estilo: una
 *   pantalla de detalle es una SEGUNDA puerta al contenido crudo de un menor, y
 *   D5 no cubre las lecturas. Lo que esta pantalla puede hacer con un mensaje
 *   largo es desplegarlo en la misma fila, que es `AW5` — no acá.
 *
 * ③ **El orden es invariante y NO se ofrece ordenar por columna.** No hay
 *   encabezados clickeables porque `ConsultaDeMensajes` no tiene parámetro de
 *   orden (⟦sin-parametro-de-orden⟧). Ordenar en el navegador reordenaría
 *   cincuenta filas de un conjunto de cientos de miles, con la página 2 por otro
 *   criterio que la 1, sin un error.
 *
 * ④ **Los dos vacíos se distinguen con `total`.** `items` vacío con `total = 0`
 *   es ~~«No hay mensajes para ese filtro»~~ ⭐ `R3-UI1` · «No hay mensajes para
 *   mostrar con este filtro.», **y es una línea DE LA TABLA**; `items` vacío con
 *   `total > 0` es una página fuera de rango y NO lleva ese texto.
 *   `ResultadoDeMensajes.total` es el total en la BASE, no `items.length`, y el
 *   número de páginas sale de él. ⭐ Ninguno de los dos hace desaparecer la tabla
 *   ni el paginado: ver ⟦el-vacio-es-una-linea-de-la-tabla⟧ abajo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * EL PAGINADO SE PIDE
 *
 * La pantalla no trae el conjunto y lo recorta: pide al borde una página con el
 * filtro aplicado y muestra lo que contesta, en el orden en que lo contesta. No
 * hay un solo `.sort(` ni `.slice(` sobre las filas. ⭐ `R3-UI1` · ~~El tamaño de
 * página entra por la configuración de la instancia (`config.pageSize`) y no se
 * elige acá~~ → **lo elige el operador con «Filas por página»** (`R3-UI0`,
 * `src/paginado.tsx`), que abre en 10 y ofrece los cinco de la escala de R3.
 */

import { useEffect, useState } from "react"
import type { JSX } from "react"
import type { MensajeListado, TamanoDePagina } from "@contracts/conversation/index.ts"
import { FILAS_POR_PAGINA_AL_ABRIR, Paginado } from "../paginado.tsx"
import { useBrazo } from "../brazo.ts"
import { esBrazo, selloDelBrazo } from "../zonas.ts"
import { useBordeConversaciones } from "../contexto-borde-conversaciones.tsx"
import { tamanoDePaginaDelContrato } from "./borde-conversaciones.ts"
import type { FiltroDeLaPantalla } from "./borde-conversaciones.ts"
import { FiltrosDeConversaciones } from "./filtros.tsx"
import { FilaDesplegable } from "./fila-desplegable.tsx"
import { BotonDescargarCsv } from "./descargar.tsx"

/**
 * «Sin filtro» es algo que se ESCRIBE — los cuatro campos en `null` — y no algo
 * que se omite. `FiltroDeLaPantalla` los declara obligatorios y anulables, así
 * que el estado inicial de la tabla es exactamente esto.
 */
const SIN_FILTRO: FiltroDeLaPantalla = {
  desde: null,
  hasta: null,
  telefono: null,
  contenido: null,
}

/**
 * Las DIEZ columnas del PRD, con esos nombres y en ese orden.
 *
 * `docs/prd/F3.md` §Pantalla «Conversaciones». El orden de este arreglo ES el
 * orden de la tabla: no hay otra lista en otro lado que pueda discrepar. No hay
 * columna de «fuentes» — la lista de nombres de documentos NO es una columna
 * (`CC3`) y vive en el mensaje desplegado de `AW5`.
 */
const COLUMNAS = [
  "id_conversacion",
  "id_sesion",
  "id_usuario",
  "telefono",
  "contenido",
  "es_sistema",
  "fecha_hora",
  "fuentes_consultadas",
  "posicion_en_sesion",
  "estado_mensaje",
] as const

/**
 * ⭐ `R1.1-F5-CA-88` · el rótulo visible de cada columna, **en el orden del PRD**:
 * *«Conversación, Sesión, Participante, Teléfono, Mensaje, ¿Del sistema?, Fecha y
 * hora, Fuentes consultadas, Posición y Estado»*.
 *
 * ⛔ Es un `Record` sobre la unión de `COLUMNAS` y no una segunda lista: si mañana
 * se agrega una columna, **`tsc` exige su rótulo acá** en vez de dejar una tabla
 * con nueve nombres y diez celdas. Dos listas paralelas son dos listas que pueden
 * divergir; ésta no puede.
 */
const ROTULO_DE_LA_COLUMNA: Record<(typeof COLUMNAS)[number], string> = {
  id_conversacion: "Conversación",
  id_sesion: "Sesión",
  id_usuario: "Participante",
  telefono: "Teléfono",
  contenido: "Mensaje",
  es_sistema: "¿Del sistema?",
  fecha_hora: "Fecha y hora",
  fuentes_consultadas: "Fuentes consultadas",
  posicion_en_sesion: "Posición",
  estado_mensaje: "Estado",
}

export function TablaConversaciones(): JSX.Element {
  const borde = useBordeConversaciones()

  /**
   * ⭐ `R3-F1-CA-07` · **El brazo sale de la DIRECCIÓN.** Mismo patrón que
   * `alta.tsx` y `metricas/pantalla.tsx`: `useBrazo()` contesta `null` cuando
   * nadie declaró ninguno, y `esBrazo` lo estrecha contra la declaración. No se
   * inventa un brazo por defecto — ver el docblock de `useBrazo`.
   */
  const brazoDeLaRuta = useBrazo()

  const [filtroAplicado, setFiltroAplicado] = useState<FiltroDeLaPantalla>(SIN_FILTRO)
  const [pagina, setPagina] = useState(1)
  const [items, setItems] = useState<readonly MensajeListado[]>([])
  const [total, setTotal] = useState(0)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)
  /*
   * ⭐ `R4-UIC1` · el aviso PROPIO de la descarga (D5/CA-14): vive separado del
   * `error` de la LISTA para que un fallo de descarga no se confunda con «no se
   * pudieron cargar los mensajes». `onIntento` lo borra al volver a tocar el
   * botón; `onFallo` lo enciende si la descarga rechaza. El identificador y su
   * setter van pegados al aviso que condicionan (`falloDescarga &&` /
   * `setFalloDescarga(false)`): el examen busca ese setter EXACTO.
   */
  const [falloDescarga, setFalloDescarga] = useState(false)

  /**
   * Cada cambio de filtro o de página es una CONSULTA NUEVA al borde.
   *
   * La bandera `cancelado` del cleanup descarta la respuesta de una consulta que
   * quedó vieja: si el operador cambia de página antes de que conteste la
   * anterior, lo que se pinta es la respuesta de la última consulta, no la que
   * llegó después de quedar obsoleta.
   */
  /**
   * ⭐⭐ `R3-UI1` · ~~⟦el-tamano-configurado-puede-no-ser-uno-de-los-cinco⟧ ·
   * `tamanoDePaginaDelContrato(config.pageSize)`~~ — **el tamaño ya no sale de la
   * configuración de la instancia**: `r3-ui1.md:53`, textual, *«el tamaño ya no
   * sale de `config.pageSize`»*, y `PRD_R3.md:721` / regla 27 (`:880`) lo ponen en
   * manos del operador. Con eso se va también la falla cerrada que `R3-SAN-UI`
   * había montado acá: **una instancia no puede volver a configurar un tamaño
   * fuera de la escala, porque ya no configura ninguno.**
   *
   * ⛔ **El valor de arranque se IMPORTA de `R3-UI0` y no se escribe.** Es
   * `FILAS_POR_PAGINA_AL_ABRIR`, cuyo docblock lo dice con esas palabras: *«el
   * valor de arranque lo elige quien tiene ese estado … lo que esta constante hace
   * es que no haya dos pantallas de R3 eligiendo dos arranques: se importa, no se
   * escribe»*. Un `10` literal acá sería el segundo lugar donde vive el 10 de
   * `PRD_R3.md:721`, y `R3-F5` pide el mismo.
   *
   * ⚠️ Al recargar vuelve a 10 —es estado de React, no de la dirección ni del
   * navegador—, que es lo que `PRD_R3.md:721` pide textualmente: *«Al recargar, la
   * pantalla vuelve a abrir con los filtros vacíos y 10 filas por página»*.
   */
  const [tamanoDePagina, setTamanoDePagina] = useState<TamanoDePagina>(FILAS_POR_PAGINA_AL_ABRIR)

  useEffect(() => {
    let cancelado = false

    setCargando(true)
    setError(null)

    void borde
      .listar({ filtro: filtroAplicado, pagina, tamanoDePagina })
      .then((resultado) => {
        if (cancelado) return
        setItems(resultado.items)
        setTotal(resultado.total)
        setCargando(false)
      })
      .catch((causa: unknown) => {
        if (cancelado) return
        setError(causa instanceof Error ? causa.message : String(causa))
        setCargando(false)
      })

    return () => {
      cancelado = true
    }
  }, [borde, filtroAplicado, pagina, tamanoDePagina])

  /**
   * Aplicar un filtro vuelve a la primera página: la página que el operador
   * estaba mirando pertenece a OTRO filtro, y quedarse en ella mostraría un
   * tramo del medio que no pidió.
   */
  const alAplicarFiltro = (filtro: FiltroDeLaPantalla) => {
    setFiltroAplicado(filtro)
    setPagina(1)
  }

  /**
   * ⭐ `R3-F1-CA-59` · «Filas por página» recalcula el paginado **con el filtro
   * aplicado**, y la vuelta a la página 1 la hace la primitiva
   * (`paginado.tsx` §`onTamanoDePagina`: *«llama TAMBIÉN a `onPagina(1)` cuando la
   * página no era la 1»*). No se repite acá: dos escrituras del mismo efecto se
   * separan en silencio, que es lo que ese docblock ya pagó.
   *
   * ⛔ **El estrechado NO es ceremonia.** `onTamanoDePagina` recibe un `number` y
   * `ConsultaDeMensajes.tamanoDePagina` es uno de cinco (`R3-CCV1`): alguien tiene
   * que estrechar, y quien lo hace es la función que `R3-SAN-UI` escribió UNA vez
   * (`tamanoDePaginaDelContrato`), atada al tipo del contrato para que el día que
   * la escala de `R3-UI0` y la del contrato diverjan, `tsc` se caiga allá y no
   * quede la pantalla pidiendo un tamaño que el borde rechaza con 4xx.
   *
   * ⚠️ Un valor fuera de la escala **no cambia nada**, y por construcción no puede
   * llegar: el selector sólo ofrece `FILAS_POR_PAGINA`. Ignorarlo es preferible a
   * sustituirlo por 10, que sería paginar distinto de lo que dice el selector.
   */
  const alElegirTamano = (elegido: number): void => {
    const delContrato = tamanoDePaginaDelContrato(elegido)
    if (delContrato !== null) setTamanoDePagina(delContrato)
  }

  /*
   * ⭐ RASTRO · `AW31` · 28/8/2026 — acá se calculaba `pageCount` a mano, una de
   * seis copias de la misma cuenta. La hace la primitiva `src/paginado.tsx`, que
   * recibe `total` y `tamanoDePagina` y NO un número de páginas ya calculado:
   * así el `guard` de «el total sale de la BASE, nunca de `items.length`» deja de
   * depender de que cada pantalla lo escriba bien por su cuenta.
   */

  /**
   * El SEGUNDO vacío, con nombre: `items` vacío **con filas en la base**. Es una
   * página que quedó fuera de rango, no un filtro sin resultados, y por eso dice
   * otra cosa. `guard` ④.
   *
   * ⛔ Se nombra en vez de escribirse dentro del JSX para que la condición del
   * PRIMER vacío (`total === 0`) y la de éste se lean una al lado de la otra: son
   * excluyentes, y leerlas separadas por ochenta líneas de tabla es cómo se llega
   * a que las dos se dibujen a la vez o ninguna.
   */
  const paginaFueraDeRango = items.length === 0 && total > 0

  return (
    <section className="conversaciones" aria-labelledby="conversaciones-titulo">
      <header className="conversaciones__encabezado">
        <h1 id="conversaciones-titulo">Conversaciones</h1>
        {/*
          `AW6` · el botón descarga con el filtro APLICADO (`filtroAplicado`), el
          mismo que produjo la respuesta en pantalla — no con el borrador del
          formulario. `guard` ②.
          ⭐ ⟦rastro · ADR-0082 · 5/10/2026 · R4-UIC1⟧ `R4-UIC1` · sobre la
          primitiva `BotonDeDescarga` (A6): `onIntento` borra el aviso de fallo al
          reintentar y `onFallo` lo enciende si el borde rechaza (D5/CA-14). El
          estado `falloDescarga` y su aviso viven acá, no en `descargar.tsx`: el
          setter `setFalloDescarga(false)` tiene que estar del lado que condiciona
          el `role="alert"` para que reintentar lo borre.
        */}
        <BotonDescargarCsv
          filtro={filtroAplicado}
          onIntento={() => setFalloDescarga(false)}
          onFallo={() => setFalloDescarga(true)}
        />
      </header>

      {falloDescarga && (
        <p role="alert" className="conversaciones__aviso-descarga">
          No se pudo descargar el CSV. Intenta de nuevo.
        </p>
      )}

      <FiltrosDeConversaciones onAplicar={alAplicarFiltro} />

      {error !== null && (
        <p className="conversaciones__error" role="alert">
          No se pudieron cargar los mensajes. {error}
        </p>
      )}

      {cargando && error === null && <p className="conversaciones__vacio">Cargando mensajes…</p>}

      {/*
        ⭐⭐ `R3-F1-CA-18` · ⟦el-vacio-es-una-linea-de-la-tabla⟧ · **LOS DOS VACÍOS
        SIGUEN SIENDO DOS, Y NINGUNO SE LLEVA LA TABLA PUESTA.**

        ~~Con `total === 0` se dibujaba un `<p>` FUERA de la tabla, y `total > 0`
        era la condición de que la tabla Y el paginado existieran.~~
        `PRD_R3.md:723` y el boceto «Híbrido · Conversaciones · filtro Contenido y
        sin resultados» piden lo contrario, textual: *«La tabla queda con una sola
        línea que dice “No hay mensajes para mostrar con este filtro.” y el
        paginado dice “Página 1 de 1 · 0 mensajes”»*.

        ⛔ **Cambiar sólo el TEXTO del `<p>` deja `CA-18` rojo igual**: el criterio
        habla de la FORMA —«la tabla queda con una sola línea»— y deja sin
        «Página 1 de 1 · 0 mensajes», que es la otra mitad de la misma frase. Es el
        `rationale_hint` textual del guard «El vacío es una línea de la tabla».

        ⭐ **`guard` ④ sobrevive entero**: los dos vacíos se siguen distinguiendo
        con `total` y no con `items.length`, y cada uno sigue diciendo una cosa
        distinta. Lo que cambió es dónde se dibujan, no cómo se distinguen.
      */}
      {!cargando && error === null && (
        <>
          <div className="tabla-rodante">
            <table className="conversaciones__tabla">
              {/*
                ⭐⭐ `R3-F1-CA-07` · **LA LEYENDA NOMBRA EL BRAZO, INTERPOLADO.**
                ~~«Mensajes entre el chatbot y los usuarios, del brazo de la sesión.
                Solo lectura.»~~ → `PRD_R3.md:677`, textual: *«Mensajes entre el
                chatbot y los participantes de KAI · Brazo X. Solo lectura. Fecha y
                hora en hora de Chile.»*.

                Tres cosas cambian y ninguna es cosmética:
                  · «usuarios» → «participantes», que es el término del glosario de R3;
                  · «del brazo de la sesión» → **el nombre del brazo**, porque el brazo
                    sale de la DIRECCIÓN desde `ADR-0136` y no de la sesión;
                  · se suma «Fecha y hora en hora de Chile.» (regla 26, `:879`).

                ⛔ **El sello se INTERPOLA y no se hornea.** Un «KAI · Brazo Híbrido»
                literal da verde en Híbrido y miente en todos los demás brazos —que
                es justo el defecto que `CA-07` mide—, y `selloDelBrazo` es la
                ÚNICA función que sabe que el punto medio es `·` (U+00B7) y no un
                guion, y que sin instancia declarada el sello es sólo el del brazo.

                ⚠️ Sin brazo en la dirección la leyenda **no inventa uno**, igual que
                Métricas (`R1.1-F4-CA-94`) y por el mismo motivo por el que `useBrazo`
                devuelve `null`: esta aplicación no puede fabricar un nombre de brazo.
              */}
              <caption className="conversaciones__caption">
                {esBrazo(brazoDeLaRuta)
                  ? `Mensajes entre el chatbot y los participantes de ${selloDelBrazo(
                      brazoDeLaRuta,
                    )}. Solo lectura. Fecha y hora en hora de Chile.`
                  : "Mensajes entre el chatbot y los participantes. Solo lectura. Fecha y hora en hora de Chile."}
              </caption>
              <thead>
                <tr>
                  {/*
                    `guard` ③ · los encabezados NO son botones: no hay orden por
                    columna. Son `<th>` planos, y `aria-sort` no aplica porque el
                    orden es invariante y lo fija el borde.
                  */}
                  {/*
                    ⭐ `R1.1-F5-CA-88` (`PRD_R1.1.md:907`) · **el encabezado es el
                    nombre en español**; la CLAVE sigue siendo la del contrato y
                    sigue mandando el orden.

                    ⛔⛔ **Y su gemelo pide lo contrario, a propósito.**
                    `R1.1-F5-CA-89` (`:908`) exige que el CSV **descargado**
                    conserve los nombres crudos: *«empieza con
                    `id_conversacion,id_sesion,id_usuario`»*. Son la PANTALLA y el
                    ARCHIVO: una la lee el operador, el otro lo consume una
                    herramienta. **Unificarlos rompe uno de los dos.**

                    ⭐ Hoy no se pueden pisar por arquitectura y no por disciplina:
                    **el CSV lo arma el servidor** —acá sólo se navega a la URL de
                    descarga (`borde-conversaciones.ts` §`descargar`)—, así que
                    esta tabla no lo toca ni puede tocarlo. El día que el CSV se
                    arme de este lado, esta separación deja de ser gratis.
                  */}
                  {COLUMNAS.map((columna) => (
                    <th key={columna} scope="col">
                      {ROTULO_DE_LA_COLUMNA[columna]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/*
                  `guard` ④ · ESTADO VACÍO, primera mitad: **una sola línea de la
                  tabla**. Se decide por `total` y no por `items.length`: con
                  `items.length` una página que quedó fuera de rango se vería como
                  «no hay mensajes» cuando el filtro tiene cientos.
                */}
                {total === 0 && (
                  <tr>
                    <td colSpan={COLUMNAS.length} className="conversaciones__vacio">
                      No hay mensajes para mostrar con este filtro.
                    </td>
                  </tr>
                )}

                {/*
                  `guard` ④ · ESTADO VACÍO, segunda mitad. `items` vacío con
                  `total > 0` es una página fuera de rango: el conjunto se achicó
                  bajo los pies del operador (otra sesión, o la API) y la página
                  que miraba dejó de existir. NO lleva el texto del filtro sin
                  resultados, que sería una afirmación falsa sobre los datos.
                */}
                {paginaFueraDeRango && (
                  <tr>
                    <td colSpan={COLUMNAS.length} className="conversaciones__vacio">
                      Esta página ya no tiene mensajes. El filtro tiene {total} en
                      total: vuelve a una página anterior.
                    </td>
                  </tr>
                )}

                {/*
                  `items` se recorre TAL COMO VINO, en el orden en que el borde lo
                  contestó. Sin `.sort()`, sin `.slice()`, sin `.filter()`: ésa es
                  la mitad visible de `guard` ③.
                */}
                {items.map((item, indice) => (
                  /*
                   * La fila no lleva `id_mensaje` a propósito (`CC1`: «lo que el
                   * PRD no nombra, no cruza el borde»), así que no hay un
                   * identificador único por fila. El índice alcanza porque la
                   * página ya viene ordenada por el borde y no hay mutación
                   * client-side: las filas se reemplazan enteras en cada consulta,
                   * nunca se reordenan ni se sacan de a una.
                   *
                   * ⛔ Y **no** se usa el identificador de Meta de `key`, que es el
                   * «natural»: la regla 11 (`PRD_R3.md:873`) dice que no se muestra
                   * en ninguna parte, y una `key` lo filtra al DOM.
                   *
                   * `FilaDesplegable` (`AW5`) expande el `contenido` in-place, sin
                   * una segunda llamada al borde.
                   */
                  <FilaDesplegable key={indice} item={item} />
                ))}
              </tbody>
            </table>
          </div>

          {/*
            PAGINADO · `guard` ④. El número de páginas se calcula con el `total`
            que trajo el borde; no se deriva de `items.length` en ningún lado. Los
            botones piden una página NUEVA — no recortan lo que ya está.

            ⭐ RASTRO · `AW31` · 28/8/2026 — acá había una barra escrita a mano, una
            de OCHO copias en siete archivos. Ahora es la primitiva `src/paginado.tsx`,
            que hace la cuenta con `total` y el tamaño de página: el `guard` deja de
            depender de que cada pantalla lo calcule bien por su cuenta.

            ⭐⭐ `R3-UI1` · **`onTamanoDePagina` es el opt-in de «Filas por página»**
            (`R3-UI0`), y con él viene «Ir a la página» por el default declarado del
            prop `conIrALaPagina`. Los dos son los elementos que `PRD_R3.md:682`
            agrega a la lista del paginado, y `CA-57`…`CA-63` los miden.

            ⛔ **El rótulo «Filas por página» NO se escribe acá** y no hay ningún
            `<select>` propio: sería la tercera copia del selector (`auditoria.tsx` y
            `envios/formulario.tsx` son las otras dos, con escala propia, y por eso
            siguen con `children`). Es el `rationale_hint` textual del guard B6.

            ⚠️ La barra se dibuja TAMBIÉN con `total` 0 —está dentro del bloque y no
            colgada de un `total` mayor que cero—: `PRD_R3.md:723` pide «Página 1 de
            1 · 0 mensajes», que es un texto que sólo existe si la barra existe.
          */}
          <Paginado
            etiqueta="mensajes"
            pagina={pagina}
            total={total}
            tamanoDePagina={tamanoDePagina}
            unidad={{ singular: "mensaje", plural: "mensajes" }}
            onPagina={setPagina}
            onTamanoDePagina={alElegirTamano}
          />

          {/*
            ⭐⭐ `R3-F1-CA-64` (`PRD_R3.md:683`, `:970`) · **LA AYUDA DICE QUÉ TRAE EL
            ARCHIVO**, textual: *«“Descargar CSV” trae todas las filas del filtro
            aplicado, en todas las páginas; sin filtro, trae todo el brazo. Fechas y
            horas en hora de Chile.»*. Es la pregunta que el operador se hace antes
            de apretar: ¿me llevo la página o el filtro entero?

            ⛔⛔ **Y LA LÍNEA DEL NOMBRE DEL ARCHIVO NO SE BORRA, SE SUMA.** El boceto
            de R3 dibuja sólo la primera frase, pero `R1.1-F1-CA-28`
            (`PRD_R1.1.md:242`) se titula *«El nombre del archivo de Conversaciones
            lleva instancia, brazo, fecha y hora»* y **sigue vigente**: R3 no lo
            deroga en ningún renglón. Las dos frases contestan preguntas distintas
            —QUÉ trae el archivo y CÓMO se va a llamar— y las dos son criterios con
            número. ⇒ van en el MISMO `<p>`, que además es lo que mantiene entera la
            forma que `aw4-tabla.test.tsx` usa de ancla (una sola
            `.conversaciones__ayuda` fuera del formulario). **La divergencia de TEXTO
            con el boceto queda reportada; derogar `CA-28` no es de esta etapa.**

            ⚠️ El nombre se dice por sus PIEZAS y no con un ejemplo con valores: un
            literal con `kai` sería falso en QA, donde la instancia es `kai-qa`. Y el
            ejemplo del boceto de R1.1 decía `conversaciones_kai_hibrido_15-09-2026.csv`
            —SIN la hora—, que viola `CA-28`: acá se transcribió el criterio.
          */}
          <p className="conversaciones__ayuda">
            «Descargar CSV» trae todas las filas del filtro aplicado, en todas las
            páginas; sin filtro, trae todo el brazo. Fechas y horas en hora de Chile.
            {" "}
            El CSV lleva en el nombre la instancia, el brazo y la fecha y hora de la descarga:
            {" "}
            <code>conversaciones_&lt;instancia&gt;_&lt;brazo&gt;_&lt;dd-mm-aaaa&gt;_&lt;hhmm&gt;.csv</code>
          </p>
        </>
      )}
    </section>
  )
}
